import React from 'react';
import { motion } from 'framer-motion';
import { C } from '../lib/palette';

// Original, hand-built 5-stage tree. Growth is driven only by consistency,
// never by weight or dose. Gentle transition + soft sway — the one place
// with expressive motion.
const spring = { type: 'spring', stiffness: 120, damping: 17 };

function Leaf({ x, y, rot, show, delay, scale = 1, tone = C.teal }) {
  return (
    <motion.path
      d={`M${x},${y} q -13,-9 0,-24 q 13,15 0,24 z`}
      transform={`rotate(${rot} ${x} ${y})`}
      fill={tone}
      stroke={C.tealDeep}
      strokeOpacity="0.18"
      initial={false}
      animate={{ opacity: show ? 1 : 0, scale: show ? scale : 0 }}
      transition={{ ...spring, delay: show ? delay : 0 }}
      style={{ transformOrigin: `${x}px ${y}px` }}
    />
  );
}

function Blossom({ x, y, show, delay }) {
  return (
    <motion.g
      initial={false}
      animate={{ opacity: show ? 1 : 0, scale: show ? 1 : 0 }}
      transition={{ ...spring, delay: show ? delay : 0 }}
      style={{ transformOrigin: `${x}px ${y}px` }}
    >
      {[0, 72, 144, 216, 288].map((a) => (
        <ellipse key={a} cx={x} cy={y - 6} rx="3.6" ry="6" fill="#E7C9D6" transform={`rotate(${a} ${x} ${y})`} />
      ))}
      <circle cx={x} cy={y} r="3" fill={C.gold} />
    </motion.g>
  );
}

export default function GrowthTree({ stage = 1, dormant = false, className = '' }) {
  const trunkH = { 1: 24, 2: 58, 3: 94, 4: 118, 5: 136 }[stage] || 24;
  const baseY = 248;
  const topY = baseY - trunkH;

  return (
    <div className={className} data-testid="companion-tree-svg" data-stage={stage} data-dormant={dormant ? '1' : '0'} style={{ opacity: dormant ? 0.9 : 1 }}>
      <svg viewBox="0 0 240 280" width="100%" role="img" aria-label={`Tree, stage ${stage} of 5`}>
        <defs>
          <linearGradient id="trunk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6E5236" />
            <stop offset="100%" stopColor="#4A3623" />
          </linearGradient>
        </defs>

        {/* ground + roots */}
        <ellipse cx="120" cy="252" rx="66" ry="10" fill={C.lineSoft} />
        <path d="M120 248 q -12 8 -26 8 M120 248 q 12 8 26 8" stroke="#4A3623" strokeWidth="2" fill="none" opacity="0.5" />

        {/* trunk */}
        <motion.path
          initial={false}
          animate={{ d: `M120,${baseY} C 115,${baseY - trunkH * 0.5} 125,${topY + 6} 120,${topY}` }}
          transition={spring}
          stroke="url(#trunk)"
          strokeWidth={stage <= 1 ? 4 : 7}
          strokeLinecap="round"
          fill="none"
        />

        {/* branches from stage 3 */}
        <motion.path initial={false} animate={{ opacity: stage >= 3 ? 1 : 0 }} transition={{ duration: 0.5 }}
          d={`M120,${topY + 34} q -28,-6 -44,-30`} stroke="url(#trunk)" strokeWidth="4" strokeLinecap="round" fill="none" />
        <motion.path initial={false} animate={{ opacity: stage >= 3 ? 1 : 0 }} transition={{ duration: 0.5 }}
          d={`M120,${topY + 46} q 28,-4 46,-26`} stroke="url(#trunk)" strokeWidth="4" strokeLinecap="round" fill="none" />

        {/* swaying canopy */}
        <motion.g
          animate={dormant ? { rotate: 0 } : { rotate: [-1.4, 1.4, -1.4] }}
          transition={dormant ? { duration: 0.6 } : { duration: 6 + stage, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: `120px ${baseY - 6}px` }}
        >
          <Leaf x={120} y={topY + 4} rot={-28} show={stage >= 1} delay={0.05} scale={0.7} tone="#3E7361" />
          <Leaf x={120} y={topY + 4} rot={28} show={stage >= 1} delay={0.1} scale={0.7} tone="#3E7361" />

          <Leaf x={120} y={topY + 30} rot={-52} show={stage >= 2} delay={0.05} />
          <Leaf x={120} y={topY + 46} rot={52} show={stage >= 2} delay={0.1} />

          <Leaf x={74} y={topY + 4} rot={-70} show={stage >= 3} delay={0.05} />
          <Leaf x={166} y={topY + 20} rot={70} show={stage >= 3} delay={0.1} />
          <Leaf x={120} y={topY - 4} rot={0} show={stage >= 3} delay={0.15} scale={1.1} />

          <Leaf x={92} y={topY - 8} rot={-40} show={stage >= 4} delay={0.05} tone="#3E7361" />
          <Leaf x={150} y={topY - 8} rot={40} show={stage >= 4} delay={0.1} tone="#3E7361" />
          <Leaf x={60} y={topY + 24} rot={-90} show={stage >= 4} delay={0.15} />
          <Leaf x={182} y={topY + 4} rot={90} show={stage >= 4} delay={0.2} />

          <Blossom x={120} y={topY - 12} show={stage >= 5} delay={0.1} />
          <Blossom x={88} y={topY + 2} show={stage >= 5} delay={0.2} />
          <Blossom x={154} y={topY} show={stage >= 5} delay={0.3} />
          <Blossom x={66} y={topY + 30} show={stage >= 5} delay={0.4} />
        </motion.g>
      </svg>
    </div>
  );
}
